import React, { useEffect, useRef, useState } from 'react';
import { searchEntities } from '../services/intelligenceApi';
import { entityLabel } from '../config/entityRoutes';
import { observableTypeLabel } from '../config/openctiObservables';
import CreateIndicatorFields from './CreateIndicatorFields';

/**
 * OpenCTI-style searchable entity picker.
 * For indicators: search existing OR create a new observable inline.
 */
function EntityPicker({
  value = null,
  onChange,
  allowedTypes = ['indicator', 'actor', 'case', 'persona', 'evidence'],
  excludeType,
  excludeId,
  placeholder = 'Search entities by name or value…',
  disabled = false,
  autoFocus = false,
  allowCreateIndicator = false,
  createCaseId,
  createActorId,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const canCreateIndicator = allowCreateIndicator && allowedTypes.includes('indicator');

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (disabled || value || creating) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const types = typeFilter === 'all' ? allowedTypes : [typeFilter];
        const res = await searchEntities({
          q: query.trim(),
          types,
          excludeType,
          excludeId,
          limit: 20,
        });
        if (!cancelled) {
          setResults(res.data.results || []);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, typeFilter, allowedTypes, excludeType, excludeId, disabled, value, creating]);

  const selectCreatedIndicator = (ioc) => {
    const entity = {
      type: 'indicator',
      id: String(ioc.indicator_id),
      label: ioc.value,
      subtitle: `${observableTypeLabel(ioc.ioc_type)} · ${ioc.confidence}`,
      justCreated: true,
    };
    onChange?.(entity);
    setCreating(false);
    setOpen(false);
    setQuery('');
  };

  if (value) {
    return (
      <div className="entity-picker entity-picker-selected">
        <div className="entity-picker-chip">
          <span className={`entity-badge entity-badge-${value.type}`}>{entityLabel(value.type)}</span>
          <div className="entity-picker-chip-text">
            <strong>{value.label}</strong>
            {value.subtitle && <span>{value.subtitle}</span>}
          </div>
          {!disabled && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                onChange?.(null);
                setQuery('');
                setCreating(false);
                setOpen(true);
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
            >
              Change
            </button>
          )}
        </div>
      </div>
    );
  }

  if (creating) {
    return (
      <div className="entity-picker entity-picker-create" ref={wrapRef}>
        <div className="entity-picker-create-title">Create new indicator</div>
        <CreateIndicatorFields
          initialValue={query.trim()}
          initialType={undefined}
          caseId={createCaseId}
          actorId={createActorId}
          disabled={disabled}
          submitLabel="Create & use"
          onCancel={() => setCreating(false)}
          onCreated={selectCreatedIndicator}
        />
      </div>
    );
  }

  return (
    <div className="entity-picker" ref={wrapRef}>
      <div className="entity-picker-toolbar">
        {allowedTypes.length > 1 && (
          <select
            className="form-input entity-picker-type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            disabled={disabled}
          >
            <option value="all">All types</option>
            {allowedTypes.map((type) => (
              <option key={type} value={type}>{entityLabel(type)}</option>
            ))}
          </select>
        )}
        <input
          ref={inputRef}
          className="form-input entity-picker-input"
          value={query}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
        />
      </div>
      {open && (
        <div className="entity-picker-menu">
          {loading && <div className="entity-picker-empty">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="entity-picker-empty">
              {query.trim() ? 'No matching entities.' : 'Type to search existing entities.'}
            </div>
          )}
          {!loading && results.map((item) => (
            <button
              key={`${item.type}:${item.id}`}
              type="button"
              className="entity-picker-option"
              onClick={() => {
                onChange?.(item);
                setOpen(false);
                setQuery('');
              }}
            >
              <span className={`entity-badge entity-badge-${item.type}`}>{entityLabel(item.type)}</span>
              <div className="entity-picker-option-text">
                <strong>{item.label}</strong>
                {item.subtitle && <span>{item.subtitle}</span>}
              </div>
            </button>
          ))}
          {canCreateIndicator && (
            <button
              type="button"
              className="entity-picker-create-action"
              onClick={() => {
                setCreating(true);
                setOpen(false);
              }}
            >
              {query.trim()
                ? `+ Create new indicator “${query.trim()}”`
                : '+ Create new indicator'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default EntityPicker;
